function normalizeHex(value: unknown, fallback: string): string {
  const candidate = String(value || '').trim();
  return /^#[0-9a-fA-F]{6}$/.test(candidate) ? candidate.toLowerCase() : fallback;
}

function readableText(hex: string): '#ffffff' | '#10201f' {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const linear = (channel: number) => channel <= 0.03928 ? channel / 12.92 : Math.pow((channel + 0.055) / 1.055, 2.4);
  const luminance = 0.2126 * linear(r) + 0.7152 * linear(g) + 0.0722 * linear(b);
  return luminance > 0.48 ? '#10201f' : '#ffffff';
}

function mixHex(base: string, target: string, targetRatio: number): string {
  const ratio = Math.max(0, Math.min(1, targetRatio));
  const channel = (hex: string, offset: number) => parseInt(hex.slice(offset, offset + 2), 16);
  const mixed = [1, 3, 5].map((offset) => Math.round(channel(base, offset) * (1 - ratio) + channel(target, offset) * ratio));
  return `#${mixed.map((value) => value.toString(16).padStart(2, '0')).join('')}`;
}

function rgba(hex: string, alpha: number): string {
  const safeAlpha = Math.max(0, Math.min(1, alpha));
  return `rgba(${parseInt(hex.slice(1, 3), 16)}, ${parseInt(hex.slice(3, 5), 16)}, ${parseInt(hex.slice(5, 7), 16)}, ${safeAlpha})`;
}

export function normalizedLogoBrightness(value?: number | string | null): number {
  const parsed = Number(value ?? 100);
  return Number.isFinite(parsed) ? Math.max(60, Math.min(260, parsed)) : 100;
}

export function tenantBrandStyle(primary?: string | null, accent?: string | null, logoBrightness?: number | string | null): Record<string, string> {
  // Saved tenant colors are authoritative. Do not silently remap a valid
  // selected color pair to a different historical default.
  const primaryColor = normalizeHex(primary, '#153f3c');
  const accentColor = normalizeHex(accent, '#9fc6bf');
  const primaryStrong = mixHex(primaryColor, '#071d1b', 0.18);
  const sidebarTop = mixHex(primaryColor, '#ffffff', 0.04);
  const sidebarBottom = mixHex(primaryColor, '#071d1b', 0.12);
  const sidebarText = readableText(sidebarTop);
  const brightness = normalizedLogoBrightness(logoBrightness);

  return {
    '--tenant-primary': primaryColor,
    '--tenant-accent': accentColor,
    '--tenant-on-primary': readableText(primaryColor),
    '--tenant-on-accent': readableText(accentColor),
    '--tenant-logo-brightness': `${brightness}%`,
    '--app-teal': primaryColor,
    '--app-teal-strong': primaryStrong,
    '--app-accent': accentColor,
    '--app-brand-soft': mixHex(primaryColor, '#ffffff', 0.92),
    '--app-brand-soft-2': mixHex(primaryColor, '#ffffff', 0.965),
    '--app-brand-border': mixHex(primaryColor, '#ffffff', 0.78),
    '--app-accent-soft': mixHex(accentColor, '#ffffff', 0.82),
    '--app-focus-ring': rgba(primaryColor, 0.12),
    '--app-sidebar-a': sidebarTop,
    '--app-sidebar-b': sidebarBottom,
    '--app-sidebar-text': sidebarText,
    '--app-sidebar-muted': rgba(sidebarText, 0.70),
    '--app-sidebar-divider': rgba(sidebarText, 0.18),
    '--app-sidebar-hover': rgba(sidebarText, 0.08),
    '--app-sidebar-active': rgba(accentColor, 0.22),
    '--app-sidebar-accent': accentColor,
    '--app-soft': mixHex(primaryColor, '#ffffff', 0.91),
  };
}
